import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import API from "../services/utils/api";
import { PATHS } from "../routes/routes.paths";
import useUserStore from "../state-management/useUserStore";
import { useRefreshOwnOrganizationConfig } from "../services/organization-config.service";
import { DetailedSubscriptionTierDto } from "../models/subscription-tier.dto";
import { invoiceOrderBy } from "../models/invoice.dto";

const subscriptionPaymentUrl = (referenceCode: string, subscriptionTierId?: string) =>
	`${PATHS.SUBSCRIPTION_PAYMENT}?reference=${encodeURIComponent(referenceCode)}` +
	(subscriptionTierId ? `&subscriptionTierId=${subscriptionTierId}` : "");

interface UseChooseSubscriptionPlanOptions {
	packages: DetailedSubscriptionTierDto[];
	/** Called before navigating to checkout, e.g. to close a modal */
	onLeave?: () => void;
	/** Called once a free package has been activated */
	onFreePackageActivated?: (subscriptionTier?: DetailedSubscriptionTierDto) => void;
	/** Called when a paid package was chosen but no invoice came back to pay */
	onNoInvoiceReturned?: () => void;
}

/**
 * Changing the organization's package, shared by the "Choose your Package" screen
 * and Account Settings > Subscription:
 * - paid packages go straight to checkout for the invoice the change raises
 * - free packages take effect immediately
 * - an organization that isn't active (e.g. suspended pending payment) is sent to
 *   pay its outstanding subscription invoice, since the backend won't change the
 *   package until then
 */
export function useChooseSubscriptionPlan({
	packages,
	onLeave,
	onFreePackageActivated,
	onNoInvoiceReturned,
}: UseChooseSubscriptionPlanOptions) {
	const navigate = useNavigate();
	const { currentUser } = useUserStore();
	const organizationId = currentUser.user.organization?.id as string;
	const refreshOrganizationConfig = useRefreshOwnOrganizationConfig();
	const [choosing, setChoosing] = useState(false);

	const goToCheckout = (referenceCode: string, subscriptionTierId?: string) => {
		onLeave?.();
		navigate(subscriptionPaymentUrl(referenceCode, subscriptionTierId));
	};

	/**
	 * Opens checkout for the organization's latest unpaid subscription invoice, if any.
	 * Unpaid means PENDING, or FAILED (left unpaid too long), which can still be retried.
	 */
	const payOutstandingInvoice = async (): Promise<boolean> => {
		try {
			const response = await API.BillingAndSubscriptionAPI.getInvoice({
				organizationId,
				productType: "SUBSCRIPTION",
				page: 1,
				limit: 10,
				orderBy: invoiceOrderBy.CREATED_AT_DESC,
			});
			const invoice = response.data?.find(
				(item) => item.status === "PENDING" || item.status === "FAILED",
			);
			if (!invoice?.referenceCode) return false;

			goToCheckout(invoice.referenceCode);
			return true;
		} catch {
			return false;
		}
	};

	const choosePlan = async (subscriptionTierId: string) => {
		if (choosing) return;
		setChoosing(true);

		const subscriptionTier = packages.find((tier) => tier.id === subscriptionTierId);
		const isFreePackage = Number(subscriptionTier?.priceAmount) === 0;

		try {
			const response = await API.BillingAndSubscriptionAPI.changeOrganizationSubscription({
				organizationId,
				subscriptionTierId,
			});
			const invoice = response?.data;

			if (!isFreePackage && invoice?.referenceCode) {
				goToCheckout(invoice.referenceCode, subscriptionTierId);
				return;
			}

			if (isFreePackage) {
				// The backend currently raises an invoice for every package change, even a
				// free one, and only switches the package once that invoice is paid. Until
				// it handles free packages, don't tell the user the switch has happened.
				await refreshOrganizationConfig();
				toast.info(
					`Your request to switch to the ${subscriptionTier?.name} package has been received.`,
				);
				onFreePackageActivated?.(subscriptionTier);
				return;
			}

			onNoInvoiceReturned?.();
		} catch (error: any) {
			if (error?.response?.data?.error !== "ORGANIZATION_NOT_ACTIVE") {
				API.Handlers.onErrorHandler(error, null, null);
				return;
			}

			const foundInvoice = await payOutstandingInvoice();
			if (foundInvoice) {
				toast.info(
					"Your account has an unpaid subscription invoice. Pay it to reactivate your account, then you can change your package.",
				);
			} else {
				toast.error(
					"Your organization isn't active, so its package can't be changed. Contact the Akatua team to reactivate it.",
				);
			}
		} finally {
			setChoosing(false);
		}
	};

	return { choosePlan, choosing, payOutstandingInvoice };
}
